// Main plan selection component for onboarding flow
import { useStores } from "@/hooks/useStores";
import { Button } from "@/src/components/ui/Button";
import { useCountryStripeProduct } from "@/src/features/pricing/hooks/useCountryStripeProduct";
import { CountryStripePriceInfo } from "@/src/features/pricing/types";
import { useState } from "react";
import { PlanSelectionDialog } from "./PlanSelectionDialog";
import { StartFreeTrialButton } from "./StartFreeTrialButton";
import { TrialDetails } from "./TrialDetails";

interface PlanSelectionProps {
  hasHadATrial: boolean;
  couponCode?: string;
  onPlanSelected?: () => void;
  currentStepIndex: number;
  logOnboardingStep: (
    currentStepIndex: number,
    currentStepKey: string,
    currentUser: any,
    otherObjective: string,
    otherText: string,
    otherCategory: string
  ) => void;
  otherObjective: string;
  otherText: string;
  otherCategory: string;
}

export const PlanSelection = ({
  hasHadATrial,
  couponCode,
  onPlanSelected,
  currentStepIndex,
  logOnboardingStep,
  otherObjective,
  otherText,
  otherCategory,
}: PlanSelectionProps) => {
  const [selectedPlan, setSelectedPlan] = useState<{
    price: CountryStripePriceInfo;
    planType: "yearly" | "monthly";
  } | null>(null);
  const { prices, yearlyPrice, isLoading } = useCountryStripeProduct();
  const { authStore } = useStores();
  const currentUser = authStore.currentUser;

  // Get the yearly price for the default display
  const defaultPrice = yearlyPrice;

  if (isLoading) {
    return (
      <div className="PlanSelection flex flex-col items-center space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-48 rounded bg-zinc-200"></div>
          <div className="h-12 w-full max-w-md rounded bg-zinc-200"></div>
          <div className="h-4 w-40 rounded bg-zinc-200"></div>
          <div className="h-10 w-32 rounded bg-zinc-200"></div>
        </div>
      </div>
    );
  }

  if (!defaultPrice) {
    return (
      <div className="PlanSelection flex flex-col items-center space-y-6">
        <p className="text-zinc-600">Unable to load pricing information.</p>
      </div>
    );
  }

  return (
    <div className="PlanSelection mt-auto flex flex-col items-center space-y-3">
      {/* No Payment Info */}
      <p className="text-center font-body text-sm">
        No payment now. Cancel anytime.
      </p>

      {/* Start Free Trial Button */}
      <StartFreeTrialButton
        selectedPlan={selectedPlan}
        defaultPrice={defaultPrice}
        hasHadATrial={hasHadATrial}
        couponCode={couponCode}
        currentUser={currentUser}
        currentStepIndex={currentStepIndex}
        logOnboardingStep={logOnboardingStep}
        otherObjective={otherObjective}
        otherText={otherText}
        otherCategory={otherCategory}
        onPlanSelected={onPlanSelected}
        className="max-w-md"
      />

      {/* Trial Details */}
      <TrialDetails selectedPlan={selectedPlan} defaultPrice={defaultPrice} />

      {/* Plan Selection Dialog with View All Plans trigger */}
      <PlanSelectionDialog
        prices={prices}
        hasHadATrial={hasHadATrial}
        couponCode={couponCode}
        currentUser={currentUser}
        currentStepIndex={currentStepIndex}
        logOnboardingStep={logOnboardingStep}
        otherObjective={otherObjective}
        otherText={otherText}
        otherCategory={otherCategory}
        onPlanSelected={(price, planType) => {
          setSelectedPlan({ price, planType });
        }}
        trigger={<Button variant="ghost">View All Plans</Button>}
      />
    </div>
  );
};
